"use client";

import React, { useState } from "react";
import { Database, Sparkles, Star, Code as Code2, TrendingUp, CircleCheck as CheckCircle2, ArrowRight, Activity, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartConfig, ChartTooltipContent } from "@/components/ui/chart";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
const STAGES = [{
  id: "evaluate",
  step: "01",
  name: "Evaluate",
  title: "AI Visibility Audit",
  description: "Comprehensive baseline assessment of how AI models like ChatGPT, Claude, and Perplexity perceive and cite your business.",
  metricLabel: "Initial Perception Score",
  metricValue: "34%",
  metricTarget: "88%"
}, {
  id: "identify",
  step: "02",
  name: "Identify",
  title: "Entity & Prompt Mapping",
  description: "Pinpoint high-intent client queries and structure your business entity data to maximize recommendation eligibility.",
  metricLabel: "Query Target Coverage",
  metricValue: "42%",
  metricTarget: "95%"
}, {
  id: "implement",
  step: "03",
  name: "Implement",
  title: "Schema & Landing Cleanup",
  description: "Deploy structured data, LLM-optimized landing pages, and review systems across core digital touchpoints.",
  metricLabel: "Structured Node Deploy",
  metricValue: "18",
  metricTarget: "120"
}, {
  id: "track",
  step: "04",
  name: "Track",
  title: "Citation Surveillance",
  description: "Continuous real-time tracking of brand citations, answer box placements, and competitor share of voice.",
  metricLabel: "Weekly AI Citations",
  metricValue: "14",
  metricTarget: "148"
}, {
  id: "optimize",
  step: "05",
  name: "Optimize",
  title: "Continuous Scaling",
  description: "Iterative schema updates and dynamic content refreshes to maintain dominance as AI algorithms change.",
  metricLabel: "Monthly Growth Delta",
  metricValue: "+12%",
  metricTarget: "+185%"
}];
const STAGE_POINTS = [{
  stageId: "evaluate",
  text: "Search engine vs LLM indexing accuracy gap analysis"
}, {
  stageId: "evaluate",
  text: "Brand sentiment and citation consistency audit"
}, {
  stageId: "evaluate",
  text: "Identification of hallucinated or missing business facts"
}, {
  stageId: "identify",
  text: "High-value local service prompt and keyword clustering"
}, {
  stageId: "identify",
  text: "Entity graph structuring for Google Gemini & Claude"
}, {
  stageId: "identify",
  text: "Schema markup blueprint for local authority signals"
}, {
  stageId: "implement",
  text: "Deployment of JSON-LD schema across primary services"
}, {
  stageId: "implement",
  text: "LLM-first page structure refactoring for direct extraction"
}, {
  stageId: "implement",
  text: "Automated client review pipeline integration"
}, {
  stageId: "track",
  text: "Weekly citation tracking across ChatGPT, Perplexity & Claude"
}, {
  stageId: "track",
  text: "Share-of-voice reporting against top local service peers"
}, {
  stageId: "track",
  text: "Direct conversion attribution from AI search referrals"
}, {
  stageId: "optimize",
  text: "Dynamic FAQ schema updates for emerging seasonal queries"
}, {
  stageId: "optimize",
  text: "Prompt alignment refactoring based on monthly telemetry"
}, {
  stageId: "optimize",
  text: "Executive strategy sessions and growth reporting"
}];
const PILLARS = [{
  id: "p1",
  step: "PILLAR 01",
  title: "LLM Indexing",
  subtitle: "Schema & Entity Nodes",
  description: "Structure core data so AI models ingest and comprehend your business accurately.",
  iconName: "Database"
}, {
  id: "p2",
  step: "PILLAR 02",
  title: "AI Citations",
  subtitle: "Authority Alignment",
  description: "Earn authoritative references in generated responses across major search LLMs.",
  iconName: "Sparkles"
}, {
  id: "p3",
  step: "PILLAR 03",
  title: "Review Velocity",
  subtitle: "Social Proof Engine",
  description: "Accelerate high-impact review collection to boost AI recommendation confidence.",
  iconName: "Star"
}, {
  id: "p4",
  step: "PILLAR 04",
  title: "Schema Architecture",
  subtitle: "Structured Data",
  description: "Deploy rich schema markup tailored specifically for generative answer extraction.",
  iconName: "Code2"
}, {
  id: "p5",
  step: "PILLAR 05",
  title: "Analytics Tracking",
  subtitle: "Conversion Telemetry",
  description: "Monitor AI referral traffic, keyword rankings, and booked calls in real time.",
  iconName: "TrendingUp"
}];
const BAR_CHART_DATA = [{
  name: "ChatGPT",
  current: 32,
  target: 88
}, {
  name: "Perplexity",
  current: 28,
  target: 92
}, {
  name: "Claude",
  current: 22,
  target: 84
}, {
  name: "Gemini",
  current: 35,
  target: 90
}];
const LINE_CHART_DATA = [{
  month: "M1",
  citations: 12,
  leads: 4
}, {
  month: "M2",
  citations: 28,
  leads: 11
}, {
  month: "M3",
  citations: 55,
  leads: 24
}, {
  month: "M4",
  citations: 89,
  leads: 42
}, {
  month: "M5",
  citations: 124,
  leads: 68
}, {
  month: "M6",
  citations: 168,
  leads: 95
}];
const chartConfig = {
  current: {
    label: "Current Reach",
    color: "hsl(17 2% 42%)"
  },
  target: {
    label: "Target Reach",
    color: "hsl(352 54% 33%)"
  },
  citations: {
    label: "AI Citations",
    color: "hsl(352 54% 33%)"
  },
  leads: {
    label: "Inbound Leads",
    color: "hsl(16 34% 74%)"
  }
} satisfies ChartConfig;
function getPillarIcon(name: string) {
  switch (name) {
    case "Database":
      return <Database className="size-5 text-[var(--accent-deep-red)]" />;
    case "Sparkles":
      return <Sparkles className="size-5 text-[var(--accent-deep-red)]" />;
    case "Star":
      return <Star className="size-5 text-[var(--accent-deep-red)]" />;
    case "Code2":
      return <Code2 className="size-5 text-[var(--accent-deep-red)]" />;
    case "TrendingUp":
      return <TrendingUp className="size-5 text-[var(--accent-deep-red)]" />;
    default:
      return <Activity className="size-5 text-[var(--accent-deep-red)]" />;
  }
}
export default function Roadmap() {
  const [activeStageIndex, setActiveStageIndex] = useState(0);
  const currentStage = STAGES[activeStageIndex];
  const currentPoints = STAGE_POINTS.filter(p => p.stageId === currentStage.id);
  return <section id="interactivegrowthroadmap" data-nav="dark" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {}
        <div className="space-y-4 max-w-3xl">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[var(--accent-deep-red)]">
            <Activity className="size-4" />
            <span>Methodology Roadmap</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground font-default">
            Engineering your dominance in AI-generated answers.
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Our 5-stage systematic framework transforms how AI search platforms ingest, verify, and recommend your service business to prospective clients.
          </p>
        </div>

        {}
        <div className="space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {STAGES.map((stage, i) => {
            const isActive = i === activeStageIndex;
            return <Button key={stage.id} data-index={i} variant={isActive ? "default" : "outline"} onClick={() => setActiveStageIndex(i)} className={`relative h-auto flex-col items-start gap-1 p-4 text-left transition-all ${isActive ? "bg-[var(--accent-deep-red)] text-white border-[var(--accent-deep-red)] shadow-md" : "bg-card text-card-foreground border-border hover:border-[var(--accent-deep-red)]/40 hover:bg-accent"}`}>
                  <div className="flex w-full items-center justify-between">
                    <span className="font-mono text-xs opacity-70">{stage.step}</span>
                    {isActive && <span className="h-2 w-2 rounded-full bg-white animate-pulse" />}
                  </div>
                  <span className="font-semibold text-sm md:text-base">{stage.name}</span>
                </Button>;
          })}
          </div>

          {}
          <AnimatePresence mode="wait">
            <motion.div key={currentStage.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.3,
            ease: "easeOut"
          }} className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
              {}
              <div className="lg:col-span-6 space-y-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-[var(--accent-deep-red)]/40 bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] font-mono text-xs">
                      STAGE {currentStage.step} OF 05
                    </Badge>
                    <span className="text-muted-foreground text-xs font-mono uppercase tracking-wider">
                      {currentStage.name} Phase
                    </span>
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight font-default">
                    {currentStage.title}
                  </h3>
                  <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                    {currentStage.description}
                  </p>
                </div>

                <Separator className="bg-border/60" />

                <div className="space-y-3">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Key Execution Milestones
                  </h4>
                  <ul className="space-y-2.5">
                    {currentPoints.map((point, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-foreground">
                        <div className="mt-0.5 rounded-full bg-[var(--accent-deep-red)]/15 p-1 text-[var(--accent-deep-red)] shrink-0">
                          <CheckCircle2 className="size-4 text-[var(--accent-deep-red)]" />
                        </div>
                        <span className="leading-snug">{point.text}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <div className="rounded-lg bg-card border border-border p-3.5 flex items-center gap-4 min-w-[220px]">
                    <div>
                      <p className="text-xs font-mono text-muted-foreground uppercase">{currentStage.metricLabel}</p>
                      <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-xl font-bold text-foreground">{currentStage.metricValue}</span>
                        <ArrowRight className="size-4 text-[var(--accent-deep-red)]" />
                        <span className="text-xl font-bold text-[var(--accent-deep-red)]">{currentStage.metricTarget}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {}
              <div className="lg:col-span-6">
                <Card className="bg-card border-border shadow-xl overflow-hidden relative">
                  <CardHeader className="pb-2 border-b border-border/50">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base font-medium flex items-center gap-2">
                        <Activity className="size-4 text-[var(--accent-deep-red)]" />
                        <span>Telemetry Monitor {"—"} {currentStage.name}</span>
                      </CardTitle>
                      <Badge variant="secondary" className="font-mono text-[10px] uppercase">
                        Live Data
                      </Badge>
                    </div>
                    <CardDescription className="text-xs text-muted-foreground">
                      Stage impact metrics and system simulation
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {activeStageIndex === 0 && <div className="space-y-6">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="rounded-lg bg-background border border-border p-4 text-center">
                            <span className="text-xs text-muted-foreground font-mono">Current Audit Score</span>
                            <div className="text-3xl font-bold text-foreground mt-1">34%</div>
                            <Progress value={34} className="mt-3 h-1.5" />
                          </div>
                          <div className="rounded-lg bg-background border border-[var(--accent-deep-red)]/30 p-4 text-center">
                            <span className="text-xs text-[var(--accent-deep-red)] font-mono">Target Optimized</span>
                            <div className="text-3xl font-bold text-[var(--accent-deep-red)] mt-1">88%</div>
                            <Progress value={88} className="mt-3 h-1.5 [&>div]:bg-[var(--accent-deep-red)]" />
                          </div>
                        </div>
                        <div className="rounded-lg bg-background/50 border border-border p-4 space-y-3">
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">ChatGPT Recognition Index</span>
                            <span className="font-mono text-foreground font-medium">Low (32%)</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Perplexity Citation Accuracy</span>
                            <span className="font-mono text-foreground font-medium">Moderate (45%)</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground">Claude Structured Knowledge</span>
                            <span className="font-mono text-foreground font-medium">Fragmented (26%)</span>
                          </div>
                        </div>
                      </div>}

                    {activeStageIndex === 1 && <ChartContainer config={chartConfig} className="h-[220px] w-full">
                        <BarChart data={BAR_CHART_DATA} margin={{
                      top: 10,
                      right: 10,
                      left: -20,
                      bottom: 0
                    }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/40" />
                          <XAxis dataKey="name" tickLine={false} axisLine={false} className="text-xs" />
                          <YAxis tickLine={false} axisLine={false} className="text-xs" />
                          <Tooltip content={<ChartTooltipContent />} />
                          <Bar dataKey="current" fill="var(--color-current)" radius={[4, 4, 0, 0]} name="Current" />
                          <Bar dataKey="target" fill="var(--color-target)" radius={[4, 4, 0, 0]} name="Target" />
                        </BarChart>
                      </ChartContainer>}

                    {activeStageIndex === 2 && <div className="space-y-4">
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span className="text-foreground font-medium">JSON-LD Schema Nodes</span>
                            <span className="font-mono text-[var(--accent-deep-red)] font-medium">85% Complete</span>
                          </div>
                          <Progress value={85} className="h-2 [&>div]:bg-[var(--accent-deep-red)]" />
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span className="text-foreground font-medium">LLM Content Restructuring</span>
                            <span className="font-mono text-foreground font-medium">60% Complete</span>
                          </div>
                          <Progress value={60} className="h-2" />
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span className="text-foreground font-medium">Review System Integration</span>
                            <span className="font-mono text-[var(--accent-deep-red)] font-medium">92% Complete</span>
                          </div>
                          <Progress value={92} className="h-2 [&>div]:bg-[var(--accent-deep-red)]" />
                        </div>
                        <div className="p-3 rounded-lg bg-background border border-border text-xs text-muted-foreground flex items-center gap-2">
                          <ShieldCheck className="size-4 text-[var(--accent-deep-red)] shrink-0" />
                          <span>All schema schemas validated against Schema.org and Google Rich Result standards.</span>
                        </div>
                      </div>}

                    {activeStageIndex === 3 && <ChartContainer config={chartConfig} className="h-[220px] w-full">
                        <AreaChart data={LINE_CHART_DATA} margin={{
                      top: 10,
                      right: 10,
                      left: -20,
                      bottom: 0
                    }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border/40" />
                          <XAxis dataKey="month" tickLine={false} axisLine={false} className="text-xs" />
                          <YAxis tickLine={false} axisLine={false} className="text-xs" />
                          <Tooltip content={<ChartTooltipContent />} />
                          <Area type="monotone" dataKey="citations" stroke="var(--color-citations)" fill="var(--color-citations)" fillOpacity={0.2} name="AI Citations" />
                          <Area type="monotone" dataKey="leads" stroke="var(--color-leads)" fill="var(--color-leads)" fillOpacity={0.1} name="Inbound Leads" />
                        </AreaChart>
                      </ChartContainer>}

                    {activeStageIndex === 4 && <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-background border border-border space-y-2">
                          <Badge variant="outline" className="border-[var(--accent-deep-red)]/30 text-[var(--accent-deep-red)] text-[10px]">
                            MONTHLY IMPACT
                          </Badge>
                          <div className="text-2xl font-bold text-foreground">+185%</div>
                          <p className="text-xs text-muted-foreground">Qualified inbound lead volume growth</p>
                        </div>
                        <div className="p-4 rounded-lg bg-background border border-border space-y-2">
                          <Badge variant="outline" className="border-[var(--accent-deep-red)]/30 text-[var(--accent-deep-red)] text-[10px]">
                            CITATION RATE
                          </Badge>
                          <div className="text-2xl font-bold text-[var(--accent-deep-red)]">4.2x</div>
                          <p className="text-xs text-muted-foreground">Higher citation probability in response boxes</p>
                        </div>
                        <div className="col-span-2 p-3 rounded-lg bg-[var(--accent-deep-red)]/10 border border-[var(--accent-deep-red)]/30 text-xs text-foreground flex items-center gap-2">
                          <Sparkles className="size-4 text-[var(--accent-deep-red)] shrink-0" />
                          <span>Automated monthly dynamic schema sync active for your domain.</span>
                        </div>
                      </div>}
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {}
        <div className="space-y-6 pt-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-border pb-4">
            <div>
              <span className="font-mono text-xs text-[var(--accent-deep-red)] uppercase tracking-widest">
                Core Pillars
              </span>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground tracking-tight font-default">
                Strategic Architecture
              </h3>
            </div>
            <p className="text-xs text-muted-foreground max-w-md">
              Five synchronized disciplines designed to position your service business as the primary AI response.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PILLARS.map((pillar, i) => <Card key={pillar.id} data-index={i} className="bg-card border-border hover:border-[var(--accent-deep-red)]/60 hover:shadow-lg transition-all duration-300 group relative overflow-hidden">
                <CardHeader className="p-5 pb-2 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] text-muted-foreground tracking-wider uppercase">
                      {pillar.step}
                    </span>
                    <div className="p-2 rounded-md bg-[var(--accent-deep-red)]/10 border border-[var(--accent-deep-red)]/20 group-hover:bg-[var(--accent-deep-red)]/20 transition-colors">
                      {getPillarIcon(pillar.iconName)}
                    </div>
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-foreground group-hover:text-[var(--accent-deep-red)] transition-colors">
                      {pillar.title}
                    </CardTitle>
                    <p className="text-xs font-mono text-muted-foreground mt-0.5">
                      {pillar.subtitle}
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="p-5 pt-0">
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {}
        <div className="rounded-xl bg-card border border-border p-8 md:p-12 text-center relative overflow-hidden space-y-6">
          <div className="max-w-2xl mx-auto space-y-3 relative z-10">
            <Badge variant="outline" className="border-[var(--accent-deep-red)]/40 bg-[var(--accent-deep-red)]/10 text-[var(--accent-deep-red)] font-mono text-xs uppercase">
              Accelerate Your AI Growth
            </Badge>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground tracking-tight font-default">
              Ready to dominate AI recommendations in your city?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base">
              Get a complete breakdown of where your business currently stands when potential clients ask AI for local recommendations.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 relative z-10">
            <Button asChild size="lg" className="bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white font-medium px-8">
              <Link to="/PricingPlansPage">Get started now</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:border-[var(--accent-deep-red)]/50 text-foreground">
              <Link to="/VisibilityCheckupPage">Get a free AI visibility checkup</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}